import React, { useState } from 'react';

import '@ab-inbev/sam-utils/src/grid-layout-shared.scss';

import AppCard from './AppCard';
import { ABISearchBar } from '@ab-inbev-labs/ux-react-components';
import { defaultRouteConfig } from '@ab-inbev/sam-layout';
import { useTranslation } from 'react-i18next';
import './App.scss';

import manifestFile from '../apps-manifest.json';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ErrorBoundary, LayoutWithLoader, Root } from '@ab-inbev/sam-components';
import { FeatureFlagNames } from '@ab-inbev/sam-feature-flags';
import { useIsCardVisible } from './utils/useIsCardVisible';
import {
  CdcManagementRoles,
  DotsRoles,
  GenericRoles,
  KpiTreeRoles,
  RecipesRoles,
  SparePartsRoles,
  SpecialsRoles
} from '@ab-inbev/sam-auth';

const dotsRoles = [GenericRoles.AppAllAdmin, ...Object.values(DotsRoles)];
const sparePartsRoles = [GenericRoles.AppAllAdmin, ...Object.values(SparePartsRoles)];
const cdcManagementRoles = Object.values(CdcManagementRoles);
const kpiTreeRoles = [GenericRoles.AppAllAdmin, ...Object.values(KpiTreeRoles)];
const specialsRoles = [GenericRoles.AppAllAdmin, ...Object.values(SpecialsRoles)];
const recipesRoles = [GenericRoles.AppAllAdmin, ...Object.values(RecipesRoles)];

type AppManifest = {
  title: Record<string, string | undefined>;
  description: Record<string, string | undefined>;
  path: string;
  cta: Record<string, string | undefined>;
};

export function AppFromManifest({ manifest }: { manifest: AppManifest[] }) {
  const i18next = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');

  const translate = (record: Record<string, string | undefined>) => {
    return record[i18next.i18n.language] ?? record['en-US'] ?? '';
  };

  const kpiCalculatorIsEnabled = useIsCardVisible({ flag: FeatureFlagNames.KPI_CALCULATOR, roles: dotsRoles });
  const liveDotsConfigurationIsEnabled = useIsCardVisible({
    flag: FeatureFlagNames.SUPPLY_DATA_MANAGEMENT_PORTAL,
    roles: dotsRoles
  });
  const sparePartsCockpitIsEnabled = useIsCardVisible({
    roles: sparePartsRoles
  });
  const cdcStreamingManagementIsEnabled = useIsCardVisible({
    flag: FeatureFlagNames.CDC_STREAMING_MANAGEMENT,
    roles: cdcManagementRoles
  });
  const kpiTreeIsEnabled = useIsCardVisible({ flag: FeatureFlagNames.KPI_TREE, roles: kpiTreeRoles });
  const specialsIsEnabled = useIsCardVisible({ roles: specialsRoles });
  const recipesIsEnabled = useIsCardVisible({ roles: recipesRoles });

  const appFilters = [
    { app: 'brewing-specials', enabled: specialsIsEnabled },
    { app: 'brewing-recipes', enabled: recipesIsEnabled },
    { app: 'supply-data-management-portal', enabled: kpiCalculatorIsEnabled || liveDotsConfigurationIsEnabled },
    { app: 'spare-parts', enabled: sparePartsCockpitIsEnabled },
    { app: 'cdc-streaming', enabled: cdcStreamingManagementIsEnabled },
    { app: 'kpi-tree', enabled: kpiTreeIsEnabled }
  ];

  const filteredCards = manifest
    .filter((app) => {
      const appFilter = appFilters.find((filter) => filter.app === app.path);
      return !appFilter || appFilter.enabled;
    })
    .filter((card) => {
      return translate(card.title).toLowerCase().includes(searchTerm.toLowerCase());
    });

  const appContent = (
    <div>
      <div className="apps-search-area">
        <ABISearchBar placeholder={i18next.t('searchApps')} onChange={setSearchTerm} />
      </div>
      <section className="grid-auto-col-count-min-width apps-grid">
        {filteredCards.map((card) => (
          <AppCard
            key={translate(card.title)}
            title={translate(card.title)}
            description={translate(card.description)}
            path={card.path}
            ctaLabel={translate(card.cta)}
          />
        ))}
      </section>
    </div>
  );

  const appRoutes = [
    {
      path: '/apps',
      element: <Root />,
      errorElement: (
        <LayoutWithLoader showUserProfile={false}>
          <ErrorBoundary />
        </LayoutWithLoader>
      ),
      children: [
        {
          index: true,
          element: appContent
        }
      ]
    }
  ];

  const router = createBrowserRouter([...defaultRouteConfig(i18next.t('appName')), ...appRoutes]);

  return <RouterProvider router={router} />;
}

function App() {
  const appsManifest: AppManifest[] = manifestFile;

  return <AppFromManifest manifest={appsManifest} />;
}

export default App;
